import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import theme from '../../theme/theme';
import Logo from '../../components/ui/icons/Logo';
import { useAuth } from '../../features/auth/hooks/useAuth';

// Brand accent-light surface (the same pale cyan used across the app, e.g. filter
// chips). Falls back to the primary tint if the token is ever absent.
const ACCENT_LIGHT = theme.filters?.chipBgColor ?? theme.palette.primary.light;

/**
 * Landing page shown at the app root — a centered welcome card over a plain
 * surface. The card greets the signed-in user; the surrounding header/side-nav
 * are the shared {@link DashboardLayout} chrome.
 */
export function HomePage() {
  const { t } = useTranslation('common');
  const { username } = useAuth();

  return (
    <Box
      sx={{
        minHeight: `calc(100vh - ${theme.toolbar.height}px)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: theme.palette.grey[100],
        p: 3,
      }}
    >
      {/* Welcome card */}
      <Paper
        elevation={8}
        sx={{
          width: '100%',
          maxWidth: theme.homePage.cardMaxWidth,
          textAlign: 'center',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.borderRadius.lg,
          bgcolor: theme.palette.common.white,
          px: { xs: 4, sm: 7 },
          pt: 6.5,
          pb: 5.5,
        }}
      >
        <Box
          sx={{
            width: theme.homePage.logoBadgeSize,
            height: theme.homePage.logoBadgeSize,
            borderRadius: '50%',
            bgcolor: ACCENT_LIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <Logo size={theme.homePage.logoSize} />
        </Box>

        <Typography
          component="h1"
          sx={{
            fontWeight: theme.typography.fontWeightSemiBold,
            fontSize: theme.homePage.title.fontSize,
            lineHeight: theme.homePage.title.lineHeight,
            letterSpacing: theme.homePage.title.letterSpacing,
            color: theme.palette.primary.main,
          }}
        >
          {t('dashboard.welcome')}
        </Typography>

        <Typography
          sx={{
            mt: 1.25,
            fontSize: theme.homePage.body.fontSize,
            lineHeight: theme.homePage.body.lineHeight,
            color: theme.palette.text.label,
          }}
        >
          {t('dashboard.welcomeMessage')}
        </Typography>

        {username && (
          <Typography
            sx={{
              mt: 3.75,
              pt: 2.75,
              borderTop: `1px solid ${theme.palette.divider}`,
              fontSize: theme.homePage.meta.fontSize,
              lineHeight: theme.homePage.meta.lineHeight,
              color: theme.palette.text.subtle,
            }}
          >
            {t('dashboard.connectedAs')}{' '}
            <Box
              component="strong"
              sx={{ color: theme.palette.text.muted, fontWeight: theme.typography.fontWeightSemiBold }}
            >
              {username}
            </Box>
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
